/**
 * Граф сценария гибридного диалога.
 * Данные, не логика — машина состояний в useHybridChat.ts интерпретирует этот граф.
 * См. спецификацию: .ai/specs/hybrid-chat.md
 */

import type { DialogStep, DialogStepId } from 'src/composables/hybrid-chat/types';

/**
 * Все шаги сценария.
 * i18n-ключи будут определены в HYBRID_CHAT.STEP.* и HYBRID_CHAT.CHIP.*
 */
export const dialogSteps: DialogStep[] = [
  // === Фаза 1: Приветствие ===
  {
    id: 'greeting',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.GREETING',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.ABOUT_US', target: 'about' },
      { labelKey: 'HYBRID_CHAT.CHIP.PROJECTS', target: 'projects' },
      { labelKey: 'HYBRID_CHAT.CHIP.SERVICES', target: 'services' },
      { labelKey: 'HYBRID_CHAT.CHIP.AI_USAGE', target: 'ai-usage' },
      { labelKey: 'HYBRID_CHAT.CHIP.PRICES', target: 'prices' },
    ],
  },

  // === Фаза 2: Информация (AI-режим) ===
  {
    id: 'about',
    mode: 'ai',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_PROJECTS', target: 'projects' },
      { labelKey: 'HYBRID_CHAT.CHIP.TO_SERVICES', target: 'services' },
      { labelKey: 'HYBRID_CHAT.CHIP.THANKS_ALL_CLEAR', target: 'farewell' },
    ],
  },
  {
    id: 'projects',
    mode: 'ai',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_ABOUT', target: 'about' },
      { labelKey: 'HYBRID_CHAT.CHIP.TO_SERVICES', target: 'services' },
      { labelKey: 'HYBRID_CHAT.CHIP.PRICES', target: 'prices' },
      { labelKey: 'HYBRID_CHAT.CHIP.THANKS_ALL_CLEAR', target: 'farewell' },
    ],
  },
  {
    id: 'services',
    mode: 'ai',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_PROJECTS', target: 'projects' },
      { labelKey: 'HYBRID_CHAT.CHIP.AI_USAGE', target: 'ai-usage' },
      { labelKey: 'HYBRID_CHAT.CHIP.PRICES', target: 'prices' },
      { labelKey: 'HYBRID_CHAT.CHIP.THANKS_ALL_CLEAR', target: 'farewell' },
    ],
  },
  {
    id: 'ai-usage',
    mode: 'ai',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_PROJECTS', target: 'projects' },
      { labelKey: 'HYBRID_CHAT.CHIP.TO_SERVICES', target: 'services' },
      { labelKey: 'HYBRID_CHAT.CHIP.THANKS_ALL_CLEAR', target: 'farewell' },
    ],
  },
  {
    id: 'prices',
    mode: 'ai',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_PROJECTS', target: 'projects' },
      { labelKey: 'HYBRID_CHAT.CHIP.THANKS_ALL_CLEAR', target: 'farewell' },
    ],
  },

  // === Фаза 3: Лид-сигнал (мягкое предложение контакта) ===
  {
    id: 'lead-offer',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.LEAD_OFFER',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.LEAVE_PHONE', target: 'collect-phone' },
      { labelKey: 'HYBRID_CHAT.CHIP.LEAVE_EMAIL', target: 'collect-email' },
      { labelKey: 'HYBRID_CHAT.CHIP.NOT_NOW', target: 'farewell' },
    ],
  },

  // === Фаза 4: Сбор контактов ===
  {
    id: 'collect-phone',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.COLLECT_PHONE',
    collect: 'phone',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.SKIP', target: 'lead-done' },
    ],
  },
  {
    id: 'collect-email',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.COLLECT_EMAIL',
    collect: 'email',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.SKIP', target: 'lead-done' },
    ],
  },

  // === Фаза 5: Завершение ===
  {
    id: 'lead-done',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.LEAD_DONE',
    chips: [
      { labelKey: 'HYBRID_CHAT.CHIP.TO_FAREWELL', target: 'farewell' },
    ],
  },
  {
    id: 'farewell',
    mode: 'script',
    textKey: 'HYBRID_CHAT.STEP.FAREWELL',
  },
];

/**
 * Быстрый доступ к шапу по ID.
 */
export const dialogStepMap = new Map<DialogStepId, DialogStep>(
  dialogSteps.map((s) => [s.id, s]),
);

/**
 * Получить шаг по ID.
 */
export const getDialogStep = (id: DialogStepId): DialogStep | undefined =>
  dialogStepMap.get(id);
